// components/TalentServices.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Container,
  Button,
  Avatar,
  Divider,
  Zoom,
  Fade,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MicIcon from "@mui/icons-material/Mic";
import CampaignIcon from "@mui/icons-material/Campaign";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EventIcon from "@mui/icons-material/Event";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedIcon from "@mui/icons-material/Verified";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(106, 17, 203, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(106, 17, 203, 0); }
  100% { box-shadow: 0 0 0 0 rgba(106, 17, 203, 0); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components
const SectionWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(10, 0),
  background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, #ffffff 100%)`,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "100%",
    background: `radial-gradient(circle at 0% 0%, ${theme.palette.primary.main}08 0%, transparent 50%),
                 radial-gradient(circle at 100% 100%, ${theme.palette.secondary.main}08 0%, transparent 50%)`,
    pointerEvents: "none",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  position: "relative",
  display: "inline-block",
  fontWeight: 800,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: -8,
    left: 0,
    width: 80,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    borderRadius: 2,
  },
}));

const ServiceCard = styled(Paper)(({ theme, index }) => ({
  padding: theme.spacing(4),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "relative",
  overflow: "hidden",
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    transform: "scaleX(0)",
    transformOrigin: "left",
  },
}));

const PremiumIconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 70,
  height: 70,
  borderRadius: 20,
  marginBottom: theme.spacing(2.5),
  background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: "white",
  boxShadow: `0 10px 25px -5px ${theme.palette.primary.main}60`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "rotate(5deg) scale(1.1)",
  },
}));

const Badge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 20,
  right: 20,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 30,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  border: `1px solid ${theme.palette.primary.main}40`,
  backdropFilter: "blur(5px)",
}));

const TalentChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  padding: theme.spacing(1.5, 0.5),
  fontSize: "1rem",
  fontWeight: 600,
  borderRadius: 30,
  background: "rgba(255, 255, 255, 0.8)",
  backdropFilter: "blur(5px)",
  border: "1px solid rgba(106, 17, 203, 0.2)",
  color: theme.palette.primary.main,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: "white",
    transform: "scale(1.08)",
    borderColor: "transparent",
    boxShadow: `0 10px 20px -5px ${theme.palette.primary.main}80`,
    "& .MuiChip-label": {
      color: "white",
    },
  },
  "& .MuiChip-label": {
    padding: theme.spacing(0, 2),
  },
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  color: "white",
  borderRadius: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 150,
    height: 150,
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
  },
}));

const CTASection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  padding: theme.spacing(6, 4),
  borderRadius: theme.spacing(4),
  color: "white",
  position: "relative",
  overflow: "hidden",
  animation: `${gradientMove} 10s ease infinite`,
  backgroundSize: "200% 200%",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "radial-gradient(circle at 20% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)",
  },
}));

const TalentServices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hoveredCard, setHoveredCard] = useState(null);
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

const services = [
    {
      icon: <PeopleAltIcon sx={{ fontSize: 35 }} />,
      title: "Talent Management",
      description:
        "360° career development with personalized strategies for long-term success in entertainment industry.",
      premium: true,
      stats: "Best Artists",
    },
    {
      icon: <MicIcon sx={{ fontSize: 35 }} />,
      title: "Artist Booking & Representation",
      description:
        "Exclusive booking network with venues and premium event access across India & International.",
      premium: true,
      stats: "Events",
    },
    {
      icon: <CampaignIcon sx={{ fontSize: 35 }} />,
      title: "PR & Media Management",
      description:
        "Strategic media presence across 50+ national & regional publications with crisis management.",
      stats: "Features",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 35 }} />,
      title: "Digital Growth & Personal Branding",
      description:
        "Data-driven social media growth, content strategy, and viral marketing campaigns.",
      stats: "High Reach",
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 35 }} />,
      title: "Brand Collaboration & Endorsement",
      description:
        "Premium brand partnerships with top labels and endorsement deal negotiations.",
      premium: true,
      stats: "Brands",
    },
    {
      icon: <EventIcon sx={{ fontSize: 35 }} />,
      title: "Event & Performance Management",
      description:
        "End-to-end production, stage management, and technical direction for flawless performances.",
      stats: "Shows",
    },
    {
      icon: <GavelIcon sx={{ fontSize: 35 }} />,
      title: "Legal & Contract Support",
      description:
        "Expert legal counsel, royalty collection, IP protection, and contract negotiation.",
      premium: true,
      stats: "Contracts",
    },
  ];
  const talentTypes = [
    { name: "Singers", icon: "🎤", color: "#FF6B6B" },
    { name: "Bands", icon: "🎸", color: "#4ECDC4" },
    { name: "DJs", icon: "🎧", color: "#9B59B6" },
    { name: "Drummers", icon: "🥁", color: "#E67E22" },
    { name: "Influencers", icon: "📱", color: "#3498DB" },
    { name: "Anchors", icon: "🎙️", color: "#F1C40F" },
    { name: "Performers", icon: "✨", color: "#E84342" },
  ];

const stats = [
    { icon: <GroupsIcon />, value: "Elite", label: "Artists Managed" },
    { icon: <EmojiEventsIcon />, value: "Champion", label: "Deals Closed" },
    { icon: <StarIcon />, value: "Outstanding", label: "Client Satisfaction" },
  ];
  return (
    <SectionWrapper id="talent-services">
      <Container maxWidth="lg">
        {/* Header Section with Premium Badge */}
        <Box sx={{ textAlign: "center", mb: 8, position: "relative" }}>
          <SectionTitle
            variant="h2"
            sx={{ fontSize: { xs: "2.5rem", md: "3.5rem" } }}
          >
            Talent Management
          </SectionTitle>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              mb: 4,
              maxWidth: "70%",
              mx: "auto",
              fontWeight: 400,
              lineHeight: 1.6,
              fontSize: { xs: "1.1rem", md: "1.5rem" }
            }}
          >
            India's most trusted talent management ecosystem, <br />
            <Box
              component="span"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              turning artists into icons
            </Box>
          </Typography>

          {/* Stats Section */}
          <Grid container spacing={3} sx={{ mt: 2, mb: 6 }}>
            {stats.map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in timeout={1000 + index * 200}>
                  <StatsCard elevation={0}>
                    <Box sx={{ fontSize: 40, mb: 0 }}>{stat.icon}</Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: "1.5rem", md: "2rem" },
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                      {stat.label}
                    </Typography>
                  </StatsCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Transparency Section - ADDED HERE with mobile responsive sizing */}
        <Box sx={{ textAlign: 'center', mb: 10, mt: 4 }}>
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{ 
              color: 'secondary.dark',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.2rem' },
              fontWeight: 700,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: { xs: 60, md: 80 },
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: 2,
              }
            }}
          >
            Transparency
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto',
              mt: 3,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
              lineHeight: 1.7,
              px: { xs: 2, md: 0 }
            }}
          >
            We believe in complete transparency in all our dealings with artists and partners. 
            No hidden fees, clear contracts, and honest communication every step of the way.
          </Typography>
          
          {/* Transparency Features */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: { xs: 2, md: 4 },
              mt: 4
            }}
          >
            {['Clear Contracts', 'No Hidden Fees', 'Regular Updates', 'Open Communication'].map((feature, idx) => (
              <Chip
                key={idx}
                icon={<VerifiedIcon />}
                label={feature}
                sx={{
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  py: { xs: 2, md: 2.5 },
                  px: { xs: 1, md: 2 },
                  background: `${theme.palette.primary.main}10`,
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: theme.palette.primary.main
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Zoom in style={{ transitionDelay: `${index * 100}ms` }}>
                <ServiceCard
                  elevation={3}
                  index={index}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {service.premium && (
                    <Badge>
                      <StarIcon
                        sx={{ fontSize: 16, color: theme.palette.primary.main }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "primary.main" }}
                      >
                        PREMIUM
                      </Typography>
                    </Badge>
                  )}
                  <PremiumIconWrapper className="service-icon">
                    {service.icon}
                  </PremiumIconWrapper>
                  <Typography
                    variant="h5"
                    component="h3"
                    gutterBottom
                    fontWeight="700"
                    sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
                  >
                    {service.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2, lineHeight: 1.7, fontSize: { xs: '0.95rem', md: '1rem' } }}
                  >
                    {service.description}
                  </Typography>
                  <Box sx={{ mt: "auto", width: "100%" }}>
                    <Divider sx={{ my: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        icon={<EmojiEventsIcon />}
                        label={service.stats}
                        size="small"
                        sx={{
                          bgcolor: `${theme.palette.primary.main}10`,
                          color: "primary.main",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                </ServiceCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* We Work With Section - Enhanced */}
        <Box sx={{ mt: 12, mb: 8, textAlign: "center" }}>
          <Divider sx={{ mb: 6 }}>
            <Chip
              icon={<GroupsIcon />}
              label="OUR TALENT NETWORK"
              sx={{
                px: 3,
                py: 2,
                fontSize: { xs: "0.9rem", md: "1.1rem" },
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                color: "white",
                "& .MuiChip-icon": { color: "white" },
              }}
            />
          </Divider>

          <Typography variant="h3" gutterBottom sx={{ 
            fontWeight: 700, 
            mb: 4,
            fontSize: { xs: '1.8rem', md: '2.5rem' }
          }}>
            We Work With{" "}
            <span
              style={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Industry's Best
            </span>
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 1, md: 2 },
              justifyContent: "center",
              mt: 4,
              mb: 6,
            }}
          >
            {talentTypes.map((talent, index) => (
              <Zoom
                in
                style={{ transitionDelay: `${index * 100}ms` }}
                key={index}
              >
                <TalentChip
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span style={{ fontSize: "1.2rem" }}>{talent.icon}</span>
                      {talent.name}
                    </Box>
                  }
                  variant="outlined"
                />
              </Zoom>
            ))}
          </Box>

          {/* Testimonial Highlight */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              bgcolor: `${theme.palette.primary.main}08`,
              borderRadius: 4,
              maxWidth: { xs: "95%", md: "80%" },
              mx: "auto",
              border: "1px solid rgba(106, 17, 203, 0.1)",
            }}
          >
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ 
                fontStyle: "italic",
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              "From independent artist to national sensation - their talent
              management ecosystem transformed my career completely."
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 2,
                gap: 1,
              }}
            >
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>SK</Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Suman Kumar Das
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Managing Director
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Enhanced CTA Section */}
        <CTASection>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: "1.8rem", md: "2.5rem" },
                }}
              >
                Ready to Elevate Your Career?
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  opacity: 0.95, 
                  mb: 3,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                Join India's most prestigious talent management network. Limited
                slots available.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => scrollToSection("contact")}
                  sx={{
                    bgcolor: "white",
                    color: "primary.main",
                    px: { xs: 3, md: 4 },
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", md: "1.1rem" },
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.9)",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  Apply for Talent Management
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection("contact")}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    px: { xs: 3, md: 4 },
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: { xs: "0.9rem", md: "1.1rem" },
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Book an Artist
                </Button>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Box
                sx={{
                  width: { xs: 150, md: 180 },
                  height: { xs: 150, md: 180 },
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: `${float} 4s ease-in-out infinite`,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <HeadsetMicIcon sx={{ fontSize: { xs: 50, md: 60 }, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    24/7 Support
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CTASection>

        {/* Trust Badges */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: { xs: 1.5, md: 4 },
            mt: 6,
            flexWrap: "wrap",
          }}
        >
          <Chip
            icon={<VerifiedIcon />}
            label="ISO Certified"
            variant="outlined"
            sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}
          />
          <Chip
            icon={<VerifiedIcon />}
            label="100% Confidential"
            variant="outlined"
            sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}
          />
          <Chip
            icon={<VerifiedIcon />}
            label="PAN India Network"
            variant="outlined"
            sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}
          />
          <Chip
            icon={<VerifiedIcon />}
            label="Global Partnerships"
            variant="outlined"
            sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}
          />
        </Box>
      </Container>
    </SectionWrapper>
  );
};

export default TalentServices;